import { createDarkTheme, createLightTheme, type Theme } from "@fluentui/react-components";
import { baeBrandRamp, baeColours, baeSemantic } from "./baeBrand";

/**
 * The BAE theme for CommerceOS.
 *
 * Fluent UI gives us accessible, well-tested behaviour — keyboard handling, focus
 * management, screen-reader semantics, a real data grid. What it must not give us is
 * Microsoft's *appearance*, because CommerceOS is a BAE product.
 *
 * Three things carry almost all of that difference, and all three are overridden below:
 *
 *  1. **Typeface.** Segoe UI is the single strongest "this is a Microsoft app" signal.
 *     Replacing it with BAE's own pairing does more than any colour change.
 *  2. **Brand colour.** The default Fluent blue (#0078D4) is instantly recognisable.
 *     The ramp is regenerated from BAE's electric blue.
 *  3. **Corner radius and density.** Fluent's defaults read as Microsoft-flat. Tightening
 *     the radius scale and the shadows moves it toward BAE's own sharper look.
 *
 * What we deliberately do *not* copy from baeinfotech.com: the 22px radii, glass surfaces
 * and radial glows. Those are right for a marketing page and wrong for a console someone
 * uses for eight hours — an operations tool needs density and calm, not atmosphere. The
 * brand here is the colour, the typography and the logo; not the marketing treatment.
 */

const baseLight = createLightTheme(baeBrandRamp);
const baseDark = createDarkTheme(baeBrandRamp);

/** BAE's display face for headings, its body face for everything else. */
const fontFamilyBase =
  '"Inter", "Inter Variable", system-ui, -apple-system, "Segoe UI", sans-serif';
const fontFamilyDisplay = '"Space Grotesk", "Inter", system-ui, -apple-system, sans-serif';
const fontFamilyMonospace = '"IBM Plex Mono", ui-monospace, "Cascadia Mono", Consolas, monospace';

/**
 * Typography, radius and shadow overrides shared by both themes.
 *
 * The radius scale is tighter than Fluent's default. A dense table of stock movements
 * reads better with near-square corners, and it is a cheap way to stop the UI looking
 * like a Microsoft product.
 */
const sharedOverrides = {
  fontFamilyBase,
  fontFamilyMonospace,
  fontFamilyNumeric: fontFamilyBase,

  borderRadiusNone: "0",
  borderRadiusSmall: "3px",
  borderRadiusMedium: "5px",
  borderRadiusLarge: "7px",
  borderRadiusXLarge: "10px",

  // Slightly softer than Fluent's defaults; shadows should separate layers, not announce
  // themselves.
  shadow2: "0 1px 2px rgba(9, 23, 37, 0.10)",
  shadow4: "0 2px 4px rgba(9, 23, 37, 0.10), 0 0 2px rgba(9, 23, 37, 0.06)",
  shadow8: "0 4px 8px rgba(9, 23, 37, 0.10), 0 0 2px rgba(9, 23, 37, 0.06)",
  shadow16: "0 8px 16px rgba(9, 23, 37, 0.12), 0 0 2px rgba(9, 23, 37, 0.06)",
  shadow28: "0 14px 28px rgba(9, 23, 37, 0.16), 0 0 8px rgba(9, 23, 37, 0.08)",
  shadow64: "0 32px 64px rgba(9, 23, 37, 0.20), 0 0 8px rgba(9, 23, 37, 0.08)",
} satisfies Partial<Theme>;

/**
 * Light theme.
 *
 * Note the brand-background override. Fluent maps `colorBrandBackground` to brand 80,
 * which here is BAE's electric blue `#1A8FFF`. White text on it measures 3.27:1 — enough
 * for large text and UI edges, but short of the 4.5:1 that body text needs, and a primary
 * button label is body text. So filled controls use brand 60 (`#0366C4`, 5.67:1) while the
 * electric blue stays the identity colour for focus rings, selection, charts and the logo.
 *
 * The brand is not diluted by this; it is applied where it reads.
 */
export const baeLightTheme: Theme = {
  ...baseLight,
  ...sharedOverrides,

  colorBrandBackground: baeBrandRamp[60],
  colorBrandBackgroundHover: baeBrandRamp[50],
  colorBrandBackgroundPressed: baeBrandRamp[40],
  colorBrandBackgroundSelected: baeBrandRamp[50],

  // Links and brand-coloured text also need to clear 4.5:1 on white.
  colorBrandForegroundLink: baeBrandRamp[60],
  colorBrandForegroundLinkHover: baeBrandRamp[50],
  colorBrandForegroundLinkPressed: baeBrandRamp[40],
  colorCompoundBrandForeground1: baeBrandRamp[60],
  colorCompoundBrandForeground1Hover: baeBrandRamp[50],
  colorCompoundBrandForeground1Pressed: baeBrandRamp[40],

  // Focus rings and selected borders are large enough for the electric blue to carry the
  // brand at its full intensity.
  colorCompoundBrandStroke: baeBrandRamp[80],
  colorCompoundBrandStrokeHover: baeBrandRamp[70],
  colorStrokeFocus2: baeBrandRamp[80],

  // A faint blue cast in the neutrals, so surfaces relate to the brand rather than
  // sitting on indifferent grey.
  colorNeutralBackground1: "#FFFFFF",
  colorNeutralBackground2: "#F7F9FC",
  colorNeutralBackground3: "#F1F4F9",
  colorNeutralForeground1: baeColours.navy,

  // Semantic palette. Fluent's Badge, MessageBar and status components read these palette
  // tokens from `color="success" | "warning" | "danger"`, so overriding the tokens is what
  // makes those components speak BAE rather than shipping Microsoft's stock green and acid
  // yellow. Every filled value below clears WCAG AA against the text Fluent places on it —
  // verified, not assumed; the worst case is 4.99:1.
  colorPaletteGreenBackground1: baeSemantic.light.successBackground,
  colorPaletteGreenBackground2: "#C9E6D8",
  colorPaletteGreenBackground3: "#0E7A4E",
  colorPaletteGreenBorder1: "#0E7A4E",
  colorPaletteGreenBorder2: "#0E6B45",
  colorPaletteGreenForeground1: baeSemantic.light.successForeground,
  colorPaletteGreenForeground2: "#0B573A",
  colorPaletteGreenForeground3: "#0E7A4E",

  colorPaletteYellowBackground1: baeSemantic.light.warningBackground,
  colorPaletteYellowBackground2: "#F3DDAF",
  colorPaletteYellowBackground3: "#E8B21F",
  colorPaletteYellowBorder1: "#E8B21F",
  colorPaletteYellowForeground1: baeSemantic.light.warningForeground,
  colorPaletteYellowForeground2: "#6F4904",

  colorPaletteRedBackground1: baeSemantic.light.dangerBackground,
  colorPaletteRedBackground2: "#F0C9C5",
  colorPaletteRedBackground3: "#B32A20",
  colorPaletteRedBorder1: "#B32A20",
  colorPaletteRedBorder2: "#A32B22",
  colorPaletteRedForeground1: baeSemantic.light.dangerForeground,
  colorPaletteRedForeground2: "#82211A",
  colorPaletteRedForeground3: "#B32A20",
};

/**
 * Dark theme.
 *
 * Grounded on the BAE navy rather than Fluent's neutral near-black, which is what makes a
 * dark console feel like this product instead of a generic dark mode. On these grounds the
 * electric blue has plenty of contrast, so it is used directly.
 */
export const baeDarkTheme: Theme = {
  ...baseDark,
  ...sharedOverrides,

  colorNeutralBackground1: "#0A1526",
  colorNeutralBackground2: "#0D1B30",
  colorNeutralBackground3: "#11223C",
  colorNeutralBackground4: "#081120",
  colorNeutralForeground1: "#E9F0FF",
  colorNeutralForeground2: "#C6D3E8",
  colorNeutralForeground3: "#93A4C7",

  colorBrandBackground: baeBrandRamp[70],
  colorBrandBackgroundHover: baeBrandRamp[80],
  colorBrandBackgroundPressed: baeBrandRamp[60],

  colorBrandForegroundLink: baeBrandRamp[110],
  colorBrandForegroundLinkHover: baeBrandRamp[120],
  colorBrandForegroundLinkPressed: baeBrandRamp[100],

  colorCompoundBrandStroke: baeBrandRamp[90],
  colorStrokeFocus2: baeBrandRamp[100],

  // Shadows carry no separation on a dark ground; borders do the work instead.
  shadow2: "0 1px 2px rgba(0, 0, 0, 0.40)",
  shadow4: "0 2px 4px rgba(0, 0, 0, 0.40)",
  shadow8: "0 4px 8px rgba(0, 0, 0, 0.45)",
  shadow16: "0 8px 16px rgba(0, 0, 0, 0.50)",
  shadow28: "0 14px 28px rgba(0, 0, 0, 0.55)",
  shadow64: "0 32px 64px rgba(0, 0, 0, 0.60)",

  // Same reasoning as the light theme, retuned for a dark ground.
  colorPaletteGreenBackground1: baeSemantic.dark.successBackground,
  colorPaletteGreenBackground2: "#1B4632",
  colorPaletteGreenBackground3: "#15794B",
  colorPaletteGreenBorder1: "#2F9A68",
  colorPaletteGreenBorder2: "#3FA877",
  colorPaletteGreenForeground1: baeSemantic.dark.successForeground,
  colorPaletteGreenForeground2: "#7ED8AC",
  colorPaletteGreenForeground3: "#5CCB96",

  colorPaletteYellowBackground1: baeSemantic.dark.warningBackground,
  colorPaletteYellowBackground2: "#4A3915",
  colorPaletteYellowBackground3: "#D9A441",
  colorPaletteYellowBorder1: "#D9A441",
  colorPaletteYellowForeground1: baeSemantic.dark.warningForeground,
  colorPaletteYellowForeground2: "#EFC272",

  colorPaletteRedBackground1: baeSemantic.dark.dangerBackground,
  colorPaletteRedBackground2: "#52251F",
  colorPaletteRedBackground3: "#C2453A",
  colorPaletteRedBorder1: "#C2453A",
  colorPaletteRedBorder2: "#D45A4E",
  colorPaletteRedForeground1: baeSemantic.dark.dangerForeground,
  colorPaletteRedForeground2: "#F5A29A",
  colorPaletteRedForeground3: "#F08479",
};

/**
 * Headings use the display face. Fluent has no display-font token, so this is exposed as a
 * plain constant for the typography components to apply.
 */
export const baeTypography = {
  fontFamilyDisplay,
  fontFamilyBase,
  fontFamilyMonospace,
} as const;
